/* eslint-disable react/jsx-filename-extension */
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { ReactSelectInput, TextInput } from '@hivebrite/design-system/Inputs';

import { ButtonCustom } from 'hb-react/frontoffice/apps/MediaCenter/components/AppHeaderButtons';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import getPageCustomizableHeaderProps from 'hb-react/shared/apps/PageCustomizables/utils/getPageCustomizableHeaderProps';
import MoreInfoButton from 'hb-react/shared/components/MoreInfoButton';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import {
  FILTERS_ID,
  FULLTEXT_FILTER_ID,
  TYPE_FILTER_ID,
  TYPE_OPTIONS,
  SORT_BY_FOR_API,
} from './rendererConstants';

const SearchIcon = () => (
  <FontAwesomeIcon height="16px" iconName="magnifying-glass" />
);

export const getSorting = (sort) => SORT_BY_FOR_API[sort.value];

const getFilterByType = (type) => ({ type: type.value });

const getFilterByFulltext = (text) => ({ fulltext: text });

const getCategoriesId = (tags) => ({
  network_category_ids: tags,
});

export const makeFetchFiles =
  (fetchFiles) =>
  ({ fetchFilters, page, signal }) =>
    axios
      .get(fetchFiles, {
        params: {
          page,
          ...(fetchFilters?.tags && getCategoriesId(fetchFilters.tags)),
          ...(fetchFilters?.sort && getSorting(fetchFilters.sort)),
          ...(fetchFilters?.filters?.type &&
            getFilterByType(fetchFilters.filters.type)),
          ...(fetchFilters?.filters?.fulltext &&
            getFilterByFulltext(fetchFilters.filters.fulltext)),
        },
        signal,
      })
      .then(({ data }) => ({
        items: data.files.map((file) => ({
          ...file,
          page: data.pagination.page,
        })),
        totalItems: data.pagination.total_items,
        page: data.pagination.page,
      }));

export const makeFetchFolders =
  (fetchFolders) =>
  ({ fetchFilters, page, signal }) =>
    axios
      .get(fetchFolders, {
        params: {
          page,
          ...(fetchFilters?.sort && getSorting(fetchFilters.sort)),
          ...(fetchFilters?.filters?.fulltext &&
            getFilterByFulltext(fetchFilters.filters.fulltext)),
        },
        signal,
      })
      .then(({ data }) => ({
        items: data.folders,
        totalItems: data.pagination.total_items,
      }));

export const makeTagsFromCategories = (categories) => {
  if (!categories) return [];

  return categories.map((category) => {
    const categoryId = `${category.id}`;

    return {
      label: category.name,
      name: categoryId,
      query: {
        [categoryId]: true,
      },
    };
  });
};

export const makeFilters = (scrollTo) => [
  {
    id: FULLTEXT_FILTER_ID,
    component: ({ id, value, onChange }) => (
      <TextInput
        clearable
        icon={SearchIcon}
        id={id}
        name={id}
        label={__('Search for resources')}
        onChange={(e) => onChange(e.target.value)}
        placeholder={__('Search by keywords')}
        value={value ?? ''}
      />
    ),
  },
  {
    id: TYPE_FILTER_ID,
    component: ({ id, value, onChange }) => (
      <ReactSelectInput
        id={id}
        name={id}
        placeholder={__('All')}
        label={s__('MediaCenter|Filter by type')}
        options={TYPE_OPTIONS}
        onChange={(selectedFilter) => {
          onChange(selectedFilter);
          if (selectedFilter) {
            scrollTo();
          }
        }}
        value={value ?? null}
        clearable
        isSearchable={false}
      />
    ),
  },
];

export const scrollToFilesBlock = (folderUuid, headerHeight) => {
  const filesId = getFilesBlockId(folderUuid);
  const filesBlock = document.getElementById(filesId);

  if (filesBlock) {
    const { top } = filesBlock.getBoundingClientRect();
    const { marginTop } = window.getComputedStyle(filesBlock);

    const topOfFilesBlock =
      top + window.scrollY - headerHeight - parseInt(marginTop, 10);

    window.scroll({ top: topOfFilesBlock, behavior: 'smooth' });
  }
};

export const getDataFiltersForFiles = ({ data }) => data[FILTERS_ID];

export const getDataFiltersForFolders = ({ data }) => {
  if (!data[FILTERS_ID]?.[TYPE_FILTER_ID]) return data[FILTERS_ID];

  // Filter by type does not apply to folders
  const typeId = TYPE_FILTER_ID;
  const { [typeId]: _typeId, ...dataWithoutTypeFilter } = data[FILTERS_ID];

  if (isEmpty(dataWithoutTypeFilter)) return null;

  return dataWithoutTypeFilter;
};

export const getFilesBlockId = (folderUuid) => {
  if (!folderUuid) return ITEMS_QUERY_KEYS.mediacenter.files;

  return `${ITEMS_QUERY_KEYS.mediacenter.files}-${folderUuid}`;
};

export const getFoldersBlockId = (folderUuid) => {
  if (!folderUuid) return ITEMS_QUERY_KEYS.mediacenter.folders;

  return `${ITEMS_QUERY_KEYS.mediacenter.folders}-${folderUuid}`;
};

export const getMediaCenterHeaderProps = ({ appPageConfig }) => {
  const { controls } = getMediaCenterHeaderControls({
    appPageConfig,
  });

  return getPageCustomizableHeaderProps({ appPageConfig, controls });
};

export const getMediaCenterHeaderControls = ({ appPageConfig }) => {
  if (!appPageConfig) {
    return {};
  }

  const {
    html_block_active: htmlBlockActive,
    html_block_content: htmlBlockContent,
    config: {
      secondary_button_enabled: secondaryButtonEnabled,
      secondary_button_url: secondaryButtonUrl,
      secondary_button_label: secondaryButtonLabel,
    },
  } = appPageConfig;

  const controls = [
    htmlBlockActive && htmlBlockContent && (
      <MoreInfoButton htmlBlock={htmlBlockContent} variant="primary" />
    ),
    secondaryButtonEnabled && secondaryButtonUrl && secondaryButtonLabel && (
      <ButtonCustom url={secondaryButtonUrl} label={secondaryButtonLabel} />
    ),
  ].filter(Boolean);

  return {
    controls,
  };
};

MoreInfoButton.propTypes = {
  htmlBlock: PropTypes.string.isRequired,
};
